import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { KundeAkte as KundeAkteDaten, Unternehmen, Angebot,
              Rechnung } from "@kundeninfo/api-client";
import { Chip, Reiter, Steckbrief } from "@kundeninfo/ui/me-design";
import { hausfarbe } from "../markenfarben.js";
import { KiRegister } from "../components/meHuellen.js";
import { ObjektVerweis } from "../components/objektPanel/ObjektVerweis.js";
import { KundenChip } from "../components/KundenChip.js";
import { UnternehmensChips, sichtFuer } from "../components/Filterkopf.js";
import { KundeStammdaten } from "./KundeStammdaten.js";
import type { StammdatenUmfeld } from "./KundeStammdaten.js";
import { KundeAnsprechpartner } from "./KundeAnsprechpartner.js";
import type { AnsprechpartnerUmfeld } from "./KundeAnsprechpartner.js";
import { KundeNotizen } from "./KundeNotizen.js";
import type { NotizenUmfeld } from "./KundeNotizen.js";
import { KundeDateien } from "./KundeDateien.js";
import type { DateienClient } from "./KundeDateien.js";
import { KundeAngebote } from "./KundeAngebote.js";
import type { AngeboteClient } from "./KundeAngebote.js";
import { KundeAuftraege } from "./KundeAuftraege.js";
import type { AuftraegeClient, Vorbelegung } from "./KundeAuftraege.js";
import { KundeRechnungen } from "./KundeRechnungen.js";
import type { RechnungenClient } from "./KundeRechnungen.js";
import { KundeWichtiges } from "./KundeWichtiges.js";
import { CustomerOverview } from "./CustomerOverview.js";
import type { OverviewClient } from "./CustomerOverview.js";
import { HeaderCard } from "./customerOverview/HeaderCard.js";
import type { MonitorKartenClient } from "./customerOverview/MonitorTile.js";
import { followTarget } from "./customerOverview/targets.js";
import { oeffneOderSpringe, useObjektPanel } from "../components/objektPanel/ObjektPanelKontext.js";
import type { ObjektBezug } from "../components/objektPanel/objektBezug.js";
import type { TargetHandlers } from "./customerOverview/targets.js";
import { AdressListe } from "./AdressListe.js";
import type { AdressClient } from "./AdressListe.js";
import { KundeKonzern } from "./KundeKonzern.js";
import type { KonzernClient } from "./KundeKonzern.js";
import type { WichtigesUmfeld } from "./KundeWichtiges.js";
import { KundeTickets } from "./KundeTickets.js";
import type { TicketsClient } from "./tickets/ticketsClient.js";
import { KundeLiefervertraege } from "./suppliers/KundeLiefervertraege.js";
import type { SupplierContractsClient } from "./suppliers/KundeLiefervertraege.js";
import { KundeEingangsrechnungen } from "./incomingInvoices/KundeEingangsrechnungen.js";
import type { IncomingListClient } from "./incomingInvoices/KundeEingangsrechnungen.js";
import { PartnerRoleBadges } from "./partners/PartnerRoleBadges.js";
import { ALL_ROLES, SUPPLIER_TABS, visibleTabs } from "./aktenReiter.js";
import { SUPPLIER_TEXT_WEB } from "./suppliers/texts.js";
import { PARTNER_TEXT } from "./partners/texts.js";
import { customerLogoSrc } from "./partners/LogoChoice.js";
import { INCOMING_TEXT_WEB } from "./incomingInvoices/texts.js";
import "./KundenAkte.css";

export type AktenTabId =
  | "uebersicht" | "stammdaten" | "adressen" | "konzern" | "ansprechpartner" | "angebote"
  | "auftraege" | "rechnungen" | "tickets" | "wichtiges" | "notizen" | "dateien"
  // Lieferanten (Aufgaben 21/22): die Reiter des Lieferanten, Sprungziele der Übersicht.
  | "liefervertraege" | "eingangsrechnungen";

/**
 * Ein Sprung aus einer Liste in die Akte: Reiter, Vorgang (Beleg, Notiz, Datei) und bei Dateien der
 * Ordner (`null` = Wurzel). `source` sagt, woher er kommt: aus der Kundenübersicht (`overview`), aus der
 * Auftrags-/Angebotssuche bzw. dem Bereich Lieferverträge/Eingangsrechnungen (`search`), vom Chip eines Vorgangs im Reiter
 * „Dateien“ (`files`), vom Sprung „Ordner“ im Abschnitt „Dateien“ eines Vorgangs (`document`), aus dem Bezug eines
 * Tickets (`ticket`), aus den Terminen (`calendar`: Liste, Zeitachse, Kalender; Terminebene T20), aus dem Cockpit
 * (`cockpit`, K27) oder aus der Liquidität (`liquidity`) — die Reiter Aufträge, Angebote und die Lieferantenreiter nennen es in ihrem Hinweis (`jumpNote`).
 */
export type AkteSprung = {
  tab: AktenTabId; vorgangId?: string; folderId?: string | null;
  source: "overview" | "search" | "files" | "document" | "ticket" | "calendar" | "cockpit" | "liquidity";
};

/** Die Tab-Leiste ist die beständige Navigationsebene innerhalb eines Kunden.
 *  Sie steht vollständig da, auch wo der Inhalt erst in einem späteren Paket kommt. */
export const AKTEN_TABS: { id: AktenTabId; label: string; paket: number }[] = [
  { id: "uebersicht",      label: "Übersicht",       paket: 1 },
  { id: "stammdaten",      label: "Stammdaten",      paket: 1 },
  // Der Reiter steht NEBEN den Stammdaten und nicht in ihnen: Adressen sind eigene Sätze,
  // die angelegt und archiviert werden — sie hängen an keinem Speichern-Knopf, und ein
  // Abschnitt im Stammdaten-Formular hätte zwei Speicherbegriffe auf einem Bildschirm.
  { id: "adressen",        label: "Adressen",        paket: 5 },
  // Direkt hinter den Adressen und vor den Ansprechpartnern: Beide sagen, WER der Kunde
  // ist — wo er sitzt und wo er hingehört —, während alles danach sagt, was mit ihm
  // geschieht. Der Router braucht dafür keine Zeile: `istAktenTab` fragt diese Liste.
  { id: "konzern",         label: "Konzern",         paket: 5 },
  { id: "ansprechpartner", label: "Ansprechpartner", paket: 2 },
  { id: "angebote",        label: "Angebote",        paket: 3 },
  { id: "auftraege",       label: "Aufträge",        paket: 3 },
  { id: "rechnungen",      label: "Rechnungen",      paket: 3 },
  // Lieferanten (Aufgabe 22, Spec „Reiter der Akte je Rolle“): hinter „Rechnungen“ — sichtbar nach Rolle und Recht
  // (`visibleTabs`, aktenReiter.ts).
  { id: "liefervertraege",    label: SUPPLIER_TEXT_WEB.tab, paket: 9 },
  { id: "eingangsrechnungen", label: INCOMING_TEXT_WEB.tab, paket: 9 },
  // Tickets (F33): hinter den Rechnungen — die Vorgänge, dann das, was zu tun ist.
  { id: "tickets",         label: "Tickets",         paket: 8 },
  { id: "wichtiges",       label: "Wichtiges",       paket: 3 },
  { id: "notizen",         label: "Notizen",         paket: 4 },
  { id: "dateien",         label: "Dateien",         paket: 4 },
];

export type KundenAkteProps = {
  kunde: KundeAkteDaten;
  activeTab: AktenTabId;
  onTab: (id: AktenTabId) => void;
  /** Für die Farbe der Kürzel-Chips im Kopf. Ohne Angabe erscheinen sie neutral. */
  companies?: Unternehmen[];
  /**
   * Das aktive Unternehmen (Spec Mandantentrennung) — Vorgabe beim Anlegen und Sicht der
   * Belegreiter. Eine fremde Akte zeichnet App gar nicht erst (FremdeAkte).
   */
  arbeitsunternehmenId?: string | null;
  /** Die Verdrahtung für den bearbeitbaren Stammdaten-Tab (Aufgabe 16): Klient,
   *  Übernahmerecht, Aktualisierungs-Rückruf und Verlassen-Wächter aus App. */
  stammdaten: StammdatenUmfeld;
  /** Dasselbe für den Ansprechpartner-Tab (Paket 2b, Aufgabe 9) — ohne
   *  `onAktualisiert`: Ansprechpartner ändern den Kundensatz im Kopf nicht. */
  ansprechpartner: AnsprechpartnerUmfeld;
  /**
   * Der Adressen-Reiter (Adressen Etappe 1, Aufgabe 8): der Klient und der Ruf „hol die
   * Akte neu". `ownerId` reicht die Akte aus `kunde.id` nach.
   *
   * `onNeuLaden` und nicht `onAktualisiert(kunde)`: Die Hauptanschrift im Kopf ist
   * ABGELEITET (sie entsteht aus der Hauptadresse, siehe adressAbleitung.ts) — die Akte
   * kennt den neuen Stand also gar nicht und kann ihn nicht nach oben reichen. Denselben
   * Kundensatz noch einmal hinaufzugeben wäre sogar wirkungslos: React verwirft ein
   * `setState` mit DERSELBEN Referenz (Object.is), und der Kopf bliebe alt stehen. Also
   * ein Zähler in App, der den Ladeeffekt der Akte erneut anwirft — dieselbe Naht wie
   * `listeVersion` für die Kundenliste.
   */
  adressen: { client: AdressClient; onNeuLaden: () => void };
  /**
   * Der Konzern-Reiter (Adressen Etappe 2, Aufgabe 5): der Klient und der Weg für den
   * frischen Kundensatz nach einer Eltern-Änderung.
   *
   * Hier `onAktualisiert(akte)` und nicht `onNeuLaden()` wie bei den Adressen: Der
   * Schreibweg antwortet mit der ganzen Akte (neue `version`, neuer `parent`), und ein
   * NEUER Satz ist auch eine neue Referenz — React verwirft ihn also nicht (der Grund,
   * warum der Adressen-Reiter einen Zähler braucht, trifft hier nicht zu). Was darüber
   * hinaus geschieht — etwa die Kundenliste neu zu laden, damit dort „Tochter von" steht
   * — entscheidet der Aufrufer in App.tsx, nicht dieser Reiter.
   */
  konzern: { client: KonzernClient; onAktualisiert: (kunde: KundeAkteDaten) => void };
  /**
   * Sprung in die Akte eines ANDEREN Kunden — aus dem Verweis im Kopf, aus der Elternkette
   * und aus den Töchtern des Konzern-Reiters.
   *
   * Er läuft über den ROUTER (App.tsx) und nicht über einen eigenen Zustand: Der Kunde ist
   * eine Ebene, kein Ziel — und die Zurück-Taste soll zurückführen, wo sie herkam.
   */
  onKunde: (id: string) => void;
  /** Der Notiz-Tab (Paket 4, Aufgabe 5): die Notizmethoden des Klienten, die Id des
   *  angemeldeten Benutzers und ob ADMIN — beides zusammen entscheidet, wer eine Notiz
   *  bearbeiten/löschen darf. `kundeId` reicht die Akte selbst aus `kunde.id` nach. */
  notizen: NotizenUmfeld;
  /** Der Dateien-Tab (Paket 4, Aufgabe 10): die Datei-/Ordner-/fileType-Methoden des
   *  Klienten. `kundeId` reicht die Akte aus `kunde.id` nach. */
  dateien: { client: DateienClient };
  /** Der Angebote-Tab (Paket 3a, Aufgabe 4): nur die Angebotsmethoden des Klienten. Die
   *  wählbaren `companies` (aus `kunde.relations` gefiltert) und die Angebot→Auftrag-Naht
   *  ergänzt die Akte selbst — die App reicht bloss den Klienten durch. */
  angebote: { client: AngeboteClient };
  /** Der Aufträge-Tab (Paket 3a, Aufgabe 7): nur die Auftragsmethoden. `companies` und die
   *  `vorbelegung` aus einem angenommenen Angebot ergänzt die Akte selbst. */
  auftraege: {
    client: AuftraegeClient;
    /** Ist die Sitzung ADMIN? Für das Lösen von Abrechnungsquellen an nicht aktiven
     *  Aufträgen (Entscheid Dirk 29.09.2026, Punkt 7). Weglassbar — dann nicht. */
    istAdmin?: boolean | undefined;
  };
  /** Der Rechnungen-Tab (Paket 3b-2, Aufgabe 4): nur die Rechnungsmethoden. `companies` und
   *  die aus dem Auftrag abgeleitete Rechnung ergänzt die Akte selbst. */
  rechnungen: { client: RechnungenClient };
  /** Der Tickets-Reiter (Tickets-Spec F33). Weglassbar — dann steht der Platzhalter. Geöffnet wird ein Ticket als Objektpanel über
   *  der Akte (Öffnen statt Springen A9–A11). */
  tickets?: { client: TicketsClient; ich: { id: string; name: string } };
  /** Der Wichtiges-Tab (Paket 3a, Aufgabe 8): die Hinweis-/Laufzeitwarnungs-Methoden.
   *  `kundeId` reicht die Akte aus `kunde.id` nach. */
  wichtiges: WichtigesUmfeld;
  /**
   * Die MONITOR-KACHEL der Übersicht (Entscheid 422, Spec „Monitor-Mandantensicht", 08.09.2026): nur die
   * beiden Klientenwege, die sie braucht; sie lädt für sich. Die Kunden-Id reicht die Akte aus `kunde.id` nach.
   *
   * Sie liegt in der ÜBERSICHT und nicht in den Stammdaten, wo die Zuordnung gepflegt wird: Sie ist eine
   * Auskunft für den ersten Blick auf einen Kunden, keine Stammdatenpflege. Ohne Mandant erscheint sie nicht.
   */
  monitor: { client: MonitorKartenClient };
  /**
   * Der Reiter Übersicht (Spec „Kundenübersicht in Kacheln“): der Klient für Abruf und Anordnung und der Weg
   * für Sprünge in einen Vorgang (Route `kunde` mit Reiter und Vorgang, App.tsx). Weglassbar — dann steht nur
   * die Kopfkachel.
   */
  uebersicht?: { client: OverviewClient; onSprung: (s: AkteSprung) => void };
  /**
   * Der Vorgang, den ein Sprung aus der Kundenübersicht (Spec „Sprünge“) aufschlagen soll: ein Angebot, ein Auftrag,
   * eine Rechnung, eine Notiz oder eine Datei. Er kommt am Navigationsziel mit (`Route.vorgangId`, siehe router.ts) und
   * wird an den Reiter durchgereicht, der gerade dran ist — die Kennung gehört immer zur Vorgangsart des Reiters, weil
   * beide aus demselben Klick stammen. Ein Reiter, der eine ihm unbekannte Kennung bekommt, tut nichts. `oeffneOrdnerId`
   * ist der Ordner einer Datei (`null` = Wurzel), `jumpSource` die Quelle des Sprungs.
   */
  oeffneVorgangId?: string;
  oeffneOrdnerId?: string | null;
  jumpSource?: AkteSprung["source"];
  /**
   * Das Unternehmen des Vorgangs, auf den ein Sprung von AUSSERHALB der Akte zeigt (Ticketbezug, Bereich Lieferverträge
   * bzw. Eingangsrechnungen; Lieferanten Aufgabe 22) — es wird zugeschaltet wie beim Sprung aus der Übersicht
   * (`followTarget`), sonst fände der Reiter den Vorgang nicht. Reist wie `oeffneVorgangId` und verfällt mit ihm.
   */
  openCompanyId?: string;
  /** Sieht der Benutzer Lieferantendaten (LEAD/ADMIN, Entscheid 496)? Ohne Angabe nein — Reiter, Ordnerknöpfe fehlen. */
  supplierView?: boolean;
  /** Die zwei Lieferantenreiter (Aufgabe 22): je der Klient des Bausteins aus Strang C bzw. D. */
  liefervertraege?: { client: SupplierContractsClient };
  eingangsrechnungen?: { client: IncomingListClient };
};

/** Id des Panels zu einem Reiter. Sie ist zugleich die Kennung des `me-reiter` (`fuer`):
 *  Das Register meldet sie in `me-reiter-gewaehlt` und erwartet sie in `aktiv`. Das Präfix
 *  hält die Id im Dokument eindeutig — „stammdaten" allein wäre als globale Id zu kurz. */
function panelId(id: AktenTabId): string {
  return `ka-panel-${id}`;
}

export function KundenAkte(p: KundenAkteProps): ReactElement {
  const companies = p.companies ?? [];
  const supplierView = p.supplierView === true;
  // Die Reiter nach Rolle der Firma und Recht des Benutzers (Spec „Reiter der Akte je Rolle“). Ohne Rollenfakten (eine
  // Schreibantwort ohne sie) gelten alle Rollen; die Lieferantenreiter brauchen trotzdem die Lieferantensicht.
  const reiter = visibleTabs(AKTEN_TABS, { roleFacts: p.kunde.roleFacts ?? ALL_ROLES, supplierView });
  // Ein nicht sichtbarer Reiter in der Adresse (etwa /kunden/<id>/liefervertraege als EDITOR) öffnet die Übersicht.
  // Nicht-null-Behauptung: Übersicht und Stammdaten sind immer sichtbar — [0] existiert immer.
  const aktiv = reiter.find((t) => t.id === p.activeTab) ?? reiter[0]!;

  // Nur die Unternehmen, bei denen dieser Kunde eine Beziehung hat — die einzigen, unter denen
  // Angebote/Aufträge angelegt und deren bestehende Kürzel aufgelöst werden. `kunde.relations`
  // trägt seit der Mandantentrennung die `companyId` (R9) — sie ist der Verbindungsschlüssel,
  // nicht mehr das Kürzel.
  const kundenUnternehmen = companies.filter(
    (c) => p.kunde.relations.some((r) => r.companyId === c.id));

  // Die SICHT der Belegreiter (Spec Mandantentrennung, E359, R19): das aktive Unternehmen samt
  // den zugeschalteten Unternehmen des Kunden — EIN Zustand für Angebote, Aufträge und Rechnungen.
  // Beim Kundenwechsel beginnt er neu: Die Wahl gehört zu EINER Kunden-Id und gilt für eine andere
  // nicht — ausdrücklich hier gesichert, nicht nur darüber, dass App die Akte zwischendurch abräumt.
  // Ein Sprung (Übersicht, Dateien, Ticketbezug, Bereich, Termin — alle über `sprungZiel`) bringt das Unternehmen seines
  // Vorgangs in der Route mit: Es gilt schon im Zeichnen, das den Sprung zeigt (Final-Fixrunde A-W1) — sonst liefe zuerst eine
  // Ladung ohne es, deren Antwort die richtige überholen könnte; der Effekt unten schreibt es danach in die Wahl.
  const companyIds = companies.map((c) => c.id);
  const canSwitchTo = (id: string): boolean => companyIds.includes(id);
  /** `ziel`: das Unternehmen des Sprungs, das die Wahl schon aufgenommen hat — danach gilt nur noch die Wahl (abwählbar). */
  const [belegWahl, setBelegWahl] = useState<{ kundeId: string; ids: string[]; ziel?: string }>(
    () => ({ kundeId: p.kunde.id, ids: [] }));
  /** Die Wahl samt einem Unternehmen — nur eines des Benutzers (ein stillgelegtes oder fremdes nicht, der Reiter öffnet dann
   *  wie sonst), nie das aktive, jedes einmal, in der Folge der Unternehmen wie bei den Chips; sonst dieselbe Liste. */
  const mitUnternehmen = (ids: string[], id: string | undefined): string[] =>
    (id === undefined || !canSwitchTo(id) || id === p.arbeitsunternehmenId || ids.includes(id) ? ids
      : [...ids, id].sort((a, b) => companyIds.indexOf(a) - companyIds.indexOf(b)));
  const eigeneWahl = belegWahl.kundeId === p.kunde.id ? belegWahl : null;
  const belegZusatz = eigeneWahl !== null && eigeneWahl.ziel === p.openCompanyId ? eigeneWahl.ids
    : mitUnternehmen(eigeneWahl?.ids ?? [], p.openCompanyId);
  const setBelegZusatz = (ids: string[]): void =>
    setBelegWahl({ kundeId: p.kunde.id, ids, ...(p.openCompanyId === undefined ? {} : { ziel: p.openCompanyId }) });
  const belegSicht = sichtFuer(p.arbeitsunternehmenId ?? null, belegZusatz);
  // Die Lieferantenreiter folgen der Mandantentrennung wie die Belegreiter (R19): derselbe Zustand, dieselben Chips.
  const istBelegReiter = aktiv.id === "angebote" || aktiv.id === "auftraege" || aktiv.id === "rechnungen"
    || SUPPLIER_TABS.includes(aktiv.id);

  /** Die Zuordnung von Notizen und Dateien (Mandantentrennung R13): alle Unternehmen für die Chips, die der
   *  Kundenbeziehungen für die Schalter, das aktive als Vorbelegung. */
  const zuordnung = { alle: companies, auswahl: kundenUnternehmen, aktivId: p.arbeitsunternehmenId ?? null };

  // Die Quelle eines Sprungs für die Reiter, die sie brauchen — nur gesetzt, wenn der Sprung sie mitbringt.
  const jumpSource = p.jumpSource === undefined ? {} : { jumpSource: p.jumpSource };

  // Objekte mit Panel (Ticket, Öffnen statt Springen A9–A11) öffnen über der Akte, sonst springen sie (F16); `onGeaendert` lädt die
  // Stelle nach dem Schließen nach (F9).
  const panels = useObjektPanel();
  const oeffneObjekt = (b: ObjektBezug, onGeaendert?: () => void): void =>
    oeffneOderSpringe(panels, b, onGeaendert === undefined ? {} : { onGeaendert });

  // Die Sprünge der Übersicht (Spec „Sprünge“): EINE Übersetzung des Ziels (followTarget), hier nur die Wege — ein Objekt mit
  // Panel öffnet über der Akte, alles andere springt über `sprungZiel` und bringt sein Unternehmen in der Route mit (PF4).
  const targetHandlers: TargetHandlers = { customerId: p.kunde.id, oeffne: (b) => oeffneObjekt(b), onKunde: p.onKunde };
  /** Ein Sprung innerhalb der Akte über die Adresse; ohne Übersicht-Props wenigstens der Reiter (Task 25, Robustheit). */
  const onSprung = (s: AkteSprung): void => (p.uebersicht === undefined ? p.onTab(s.tab) : p.uebersicht.onSprung(s));

  // Das Unternehmen des Sprungs bleibt zugeschaltet, auch wenn die Route es mit dem Vorgang wieder verliert (Reiterwechsel).
  // Ein schon zugeschaltetes und das aktive ändern nichts — derselbe Zustand, kein Neuzeichnen.
  const targetCompany = p.openCompanyId;
  useEffect(() => {
    if (targetCompany === undefined) return;
    // Das aktive Unternehmen kommt nie in die Wahl: dann ändert sich nichts (kein Neuzeichnen).
    if (targetCompany === p.arbeitsunternehmenId || !canSwitchTo(targetCompany)) return;
    setBelegWahl((w) => {
      const ids = w.kundeId === p.kunde.id ? w.ids : [];
      return { kundeId: p.kunde.id, ids: mitUnternehmen(ids, targetCompany), ziel: targetCompany };
    });
    // Nur beim neuen Ziel; der Setzer liest den aktuellen Kunden selbst.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetCompany, p.kunde.id]);

  /** „Ordner“ am Vorgang (Aufgabe 25) schlägt den Reiter „Dateien“ in diesem Ordner auf — mit eigener Quelle. */
  const openFolder = (folderId: string): void => onSprung({ tab: "dateien", folderId, source: "document" });

  /** Der Ticketweg der Lieferantenobjekte (PF19): „Ticket anlegen“ nur mit dem Ticketweg der Akte. */
  const ticket = p.tickets === undefined ? {}
    : { ticket: { client: p.tickets.client, ich: p.tickets.ich, arbeitsunternehmenId: p.arbeitsunternehmenId ?? null } };
  // Neues nur im aktiven Unternehmen mit Lieferantenrolle (auch ehemalig: Schlussrechnung) — sonst gesperrt mit dem Kürzel
  // im Satz (A-W6); der Server prüft dasselbe (`KEINE_LIEFERANTENROLLE`).
  const activeRelation = p.kunde.relations.find((r) => r.companyId === p.arbeitsunternehmenId);
  const activeShortCode = companies.find((c) => c.id === p.arbeitsunternehmenId)?.shortCode ?? "";
  const supplierTabProps = {
    customerId: p.kunde.id, sicht: belegSicht, aktivesUnternehmen: p.arbeitsunternehmenId ?? "",
    // Ladefehler zeigen die Reiter selbst als Hinweis; die Akte braucht sie nicht weiter.
    onFehler: () => undefined, unternehmen: companies, onOrdnerOeffnen: openFolder,
    oeffneId: p.oeffneVorgangId ?? null, ...jumpSource, ...ticket,
    missingRoleAt: activeRelation?.supplierStatus === undefined || activeRelation.supplierStatus === null
      ? activeShortCode : null,
  };

  // Die Angebot→Auftrag-Naht (Ruling P3A-B): Klickt der Angebote-Reiter „Auftrag anlegen", legt
  // er hier eine Vorbelegung ab und die Akte wechselt auf den Aufträge-Reiter, der sie beim
  // Mounten aufnimmt. KundenAkte hält den Zustand — der Angebote-Reiter erzeugt keinen Auftrag.
  const [vorbelegung, setVorbelegung] = useState<Vorbelegung | null>(null);

  // Die Auftrag→Rechnung-Naht (Paket 3b-2): Der Aufträge-Reiter LEITET die Rechnung ab und
  // meldet den Entwurf hier herauf; die Akte merkt sich seine Kennung und wechselt in den
  // Rechnungen-Reiter, der ihn beim Laden aufschlägt. Die Akte hält den Zustand — dieselbe
  // Aufgabenteilung wie bei der Angebot→Auftrag-Naht darüber.
  const [neueRechnungId, setNeueRechnungId] = useState<string | null>(null);

  function auftragAusAngebot(a: Angebot): void {
    setVorbelegung({
      offerId: a.id,
      companyId: a.companyId,
      title: a.title,
      netAmount: a.netAmount,
      contactId: a.contactId,
      responsibleUserId: a.responsibleUserId,
      periodBasis: a.periodBasis,
    });
    p.onTab("auftraege");
  }

  // Die Vorbelegung verfällt, sobald der Aufträge-Reiter verlassen wird. KundeAuftraege liest sie
  // nur beim Mounten; bliebe sie stehen, öffnete ein späterer Rücksprung auf den Reiter erneut
  // die vorbelegte Anlage. setVorbelegung(null) ist unschädlich, wenn ohnehin schon null.
  useEffect(() => {
    if (p.activeTab !== "auftraege") setVorbelegung(null);
  }, [p.activeTab]);

  function rechnungAusAuftrag(r: Rechnung): void {
    setNeueRechnungId(r.id);
    p.onTab("rechnungen");
  }

  // Und dieselbe Verfallsregel: Der Rechnungen-Reiter liest die Kennung beim Laden. Bliebe
  // sie stehen, schlüge ein späterer Rücksprung auf den Reiter erneut denselben Entwurf auf.
  useEffect(() => {
    if (p.activeTab !== "rechnungen") setNeueRechnungId(null);
  }, [p.activeTab]);

  // Spec „Barrierefreiheit“: Nach einem Sprung liegt der Fokus am Ziel. Der Sprung baut die Übersicht samt der angeklickten
  // Zeile ab, der Fokus stünde danach auf `body`; dann nimmt ihn das Panel des neuen Reiters (ohne zu rollen). Was ihn danach
  // selbst nimmt, bekommt ihn von dort: der Dialog des Belegs, die Notiz oder Datei des Sprungs. Ein Reiterklick und jeder
  // andere Fokus bleiben, wo sie sind; beim ersten Zeichnen der Akte geschieht nichts.
  const panel = useRef<HTMLDivElement | null>(null);
  const shownTab = useRef(aktiv.id);
  useEffect(() => {
    if (shownTab.current === aktiv.id) return;
    shownTab.current = aktiv.id;
    const active = document.activeElement;
    if (active === null || active === document.body) panel.current?.focus({ preventScroll: true });
  }, [aktiv.id]);

  // Reiter → Akte: Das Register setzt `aktiv` beim Klick und bei der Tastatur selbst und
  // meldet die Kennung (= Panel-Id). Pfeiltasten, Home/Ende, Roving tabindex und der Überlauf
  // in „…" liegen seit ME-Etappe 2 (Aufgabe 4b) im Element, nicht mehr hier.
  function reiterGewaehlt(kennung: string): void {
    const tab = reiter.find((t) => panelId(t.id) === kennung);
    if (tab !== undefined) p.onTab(tab.id);
  }

  const logoSrc = customerLogoSrc(p.kunde);
  const hatLogo = logoSrc !== null;

  return (
    <div className="ka">
      <header className="ka__kopf">
        <Steckbrief
          art="spalte"
          // Die Akte ist die Hauptsache der Seite: Ihr Titel ist das h1 (Review A4 W1,
          // me-design fc7f4c8 `ueberschrift`). Vorgabe des Elements wäre h2.
          ueberschrift={1}
          titel={p.kunde.companyName}
          // Nur zur Wiedererkennung, klein. Lädt das Logo nicht (404, Datei weg), wird
          // das Bild ausgeblendet — KEIN Platzhalter (Spec). Aus der eigenen Domäne
          // ausgeliefert und dort mit Riegel-2-Kopfzeilen versehen (routes/logo.ts).
          // `iconArt="bild"` (Entscheid Dirk 24, me-design 3311f91): Kasten ohne Fläche,
          // das Logo auf Kastenhöhe eingepasst, höchstens doppelt so breit — die Maße bringt
          // das Element mit; `ka__logo` ist nur noch Kennzeichen.
          // Die Adresse trägt die Fassung (`customerLogoSrc`, Entscheid 673): Ein ersetztes Logo lädt neu; `key` hebt
          // ein früheres Ausblenden auf.
          {...(hatLogo ? { iconArt: "bild" as const } : {})}
          icon={hatLogo && (
            <img
              key={p.kunde.version}
              className="ka__logo"
              src={logoSrc ?? undefined}
              alt=""
              onError={(e) => { e.currentTarget.hidden = true; }}
            />
          )}
          // Die HAUPTANSCHRIFT (Adressen Etappe 1), die „Teil von <Eltern>"-Zeile
          // (Adressen Etappe 2) und die Kundennummern-Chips (Aufgabe 4b) stehen im Slot
          // `werte`: Alle sind Kennwerte des Kunden, die zu jedem Reiter gehören und
          // nicht zu einem. Ohne alle drei bleibt
          // der Slot leer (kein Container im Shadow DOM) statt eine leere Fläche zu
          // zeigen — `undefined` statt eines leeren Fragments.
          werte={p.kunde.hauptanschriftZeilen.length > 0
            || (p.kunde.parent !== null && p.kunde.parent !== undefined)
            || p.kunde.relations.length > 0 ? (
            <>
              {/* Sie kommt FERTIG als Zeilen vom Server (abgeleitet aus der
                  Hauptadresse, siehe adressAbleitung.ts); die Akte setzt sie nicht aus
                  flachen Feldern zusammen, sonst stünde hier eine zweite, alternde
                  Fassung des Blockbaus. Ohne Adresse steht gar nichts da — keine Lücke
                  mit Bindestrichen. */}
              {p.kunde.hauptanschriftZeilen.length > 0 && (
                <address className="ka__anschrift" role="group" aria-label="Anschrift">
                  {p.kunde.hauptanschriftZeilen.map((z, i) => (
                    <span key={`anschrift-${i}`} className="ka__anschriftzeile">{z}</span>
                  ))}
                </address>
              )}
              {/* Wo dieser Kunde im Konzern hängt, gehört zu jedem Reiter und nicht
                  hinter einen. Bei einem nicht einsehbaren Elternteil steht nur die
                  TATSACHE da (Festlegung F1) — ohne Namen und ohne Sprung: Ein Ziel,
                  das mit 404 antwortete, wäre schlimmer als ein Text. Ohne Elternteil
                  steht hier gar nichts. */}
              {p.kunde.parent !== null && p.kunde.parent !== undefined && (
                <div className="ka__eltern">
                  {/* Die Holding ist ein fremdes Objekt (115, „Öffnen statt Springen“ E5): Text, der Sprung in ihre Akte
                      direkt dahinter („Zur Akte ‹Holding›“). */}
                  {p.kunde.parent.sichtbar && p.kunde.parent.id !== null ? (<>
                    <span>Teil von {p.kunde.parent.companyName}</span>
                    <ObjektVerweis bezug={{ art: "akte", id: p.kunde.parent.id, reiter: "uebersicht",
                      label: p.kunde.parent.companyName ?? "" }} />
                  </>) : (
                    <span className="ka__eltern-verdeckt">
                      Teil eines übergeordneten Kunden — nicht einsehbar
                    </span>
                  )}
                  {p.kunde.parent.archiviert && (
                    <Chip className="ka__eltern-marke" statusArt="warnung">Eltern archiviert</Chip>)}
                </div>
              )}
              {/* Kürzel + Kundennummer je Unternehmensbeziehung (Aufgabe 4b): Kennwerte des
                  Kunden wie Anschrift und Konzernzeile, darum im selben Slot darunter. Eine
                  einzelne `customerNumber` am Kunden gibt es nicht — sie steht je Beziehung
                  in `relations[]`. */}
              {/* Dahinter die Rollen je Kürzel als Kennzeichen (Spec „Kennzeichen, Listen und Filter“, Aufgabe 22). */}
              {p.kunde.relations.length > 0 && (
                <div className="ka__chips" role="group" aria-label="Kundennummern">
                  {p.kunde.relations.map((r) => (
                    <span key={r.shortCode} className="ka__relation">
                      <KundenChip r={r} colorHex={hausfarbe(companies, r.shortCode)} />
                      <PartnerRoleBadges relation={r} mitKuerzel={false} />
                    </span>
                  ))}
                </div>
              )}
            </>
          ) : undefined}
        />
      </header>

      {/* Die Reiterleiste (ME-Etappe 2, Aufgabe 4b): `me-register` mit einem `me-reiter` je
          Bereich, Reiter in Inhaltsbreite, was nicht passt, liegt hinter „…". Der aktive
          Reiter liegt auf der Unterlinie der Leiste und trägt --grund — das Panel darunter
          trägt dieselbe Fläche, beide bilden eine geschlossene Fläche (Dirk, 08.10.2026).
          `role="tablist"` und `role="tab"` stehen im Shadow DOM der Elemente; `fuer` setzt
          dort `aria-controls`, das über die Schattengrenze aber nicht auf das Panel zeigt —
          das Panel trägt deshalb seinen Namen selbst (`aria-label`). */}
      {/* `aria-label` am Host spiegelt me-register (ab 7bc0431) auf seine tablist im Shadow
          DOM — der zugängliche Name der Leiste wie vor Aufgabe 4b. */}
      <KiRegister className="ka__register" aria-label={PARTNER_TEXT.recordTabs}
        aktiv={panelId(aktiv.id)}
        onReiterGewaehlt={(d) => reiterGewaehlt(d.id)}>
        {reiter.map((t) => (
          <Reiter key={t.id} fuer={panelId(t.id)} beschriftung={t.label} />
        ))}
      </KiRegister>

      {/* Der Tab-Wechsel tauscht ausschließlich diesen einen Bereich — Kopf und Tab-Leiste
          bleiben stehen. "Der Kunde ist eine Ebene, kein Ziel." */}
      <div role="tabpanel" id={panelId(aktiv.id)} className="ka__panel" aria-label={aktiv.label} tabIndex={-1} ref={panel}>
        {istBelegReiter && (
          <UnternehmensChips label="Belege nach Unternehmen" companies={kundenUnternehmen}
            aktivId={p.arbeitsunternehmenId ?? null} zusatz={belegZusatz} onZusatz={setBelegZusatz} />
        )}
        {/* Welche Tabs Inhalt haben, sagt ausschliesslich dieser Zweig — je gebauter Bereich
            eine eigene Bedingung, alle übrigen fallen auf den Platzhalter mit ihrer
            Paketnummer. Bewusst KEINE einzelne „bis Paket N"-Grenze mehr (früher
            FERTIGE_PAKETE): Paket 4 (Notizen/Dateien) ist fertig, während Paket 3
            (Angebote/Aufträge/Wichtiges) noch aussteht — eine fortlaufende Zahl könnte
            „1, 2 und 4 gebaut, 3 offen" nicht ausdrücken und liesse die Paket-3-Tabs
            fälschlich Inhalt statt ihres Platzhalters zeigen. */}
        {aktiv.id === "stammdaten"
          ? <KundeStammdaten kunde={p.kunde} companies={companies} {...p.stammdaten} />
          : aktiv.id === "adressen"
            // `key` am Kunden: Der Baustein hält seinen eigenen Zustand (Liste, Panel), und
            // beim Kundenwechsel soll nichts davon überleben — dieselbe Vorsorge wie bei
            // der Übersicht.
            ? <AdressListe key={p.kunde.id} client={p.adressen.client}
                besitzer="customers" ownerId={p.kunde.id} titel="Adressen"
                namensVorschlag={p.kunde.companyName}
                onGeaendert={p.adressen.onNeuLaden} />
          : aktiv.id === "konzern"
            // `key` am Kunden — aus demselben Grund wie beim Adressen-Reiter darüber, und
            // hier noch dringlicher: Dieser Reiter führt SELBST in eine andere Akte.
            ? <KundeKonzern key={p.kunde.id} client={p.konzern.client} kunde={p.kunde}
                onKunde={p.onKunde} onAktualisiert={p.konzern.onAktualisiert}
                companies={companies} />
          : aktiv.id === "ansprechpartner"
            ? <KundeAnsprechpartner kundeId={p.kunde.id}
                {...p.ansprechpartner} />
            : aktiv.id === "angebote"
              // `kundeName` allein für die Platzhalter-Vorschau der Angebotstexte
              // (#{kunde.name}) — der Reiter lädt den Kunden nicht selbst.
              ? <KundeAngebote kundeId={p.kunde.id} client={p.angebote.client}
                  companies={kundenUnternehmen} companyIds={belegSicht}
                  onAuftragAusAngebot={auftragAusAngebot}
                  oeffneAngebotId={p.oeffneVorgangId ?? null}
                  {...jumpSource}
                  arbeitsunternehmenId={p.arbeitsunternehmenId ?? null}
                  kundeName={p.kunde.companyName} onOrdnerOeffnen={openFolder} />
              : aktiv.id === "auftraege"
                // `kundeName` auch hier allein für die Platzhalter-Vorschau der
                // Auftragstexte (#{kunde.name}) — wie am Angebote-Reiter.
                ? <KundeAuftraege kundeId={p.kunde.id} client={p.auftraege.client}
                    companies={kundenUnternehmen} companyIds={belegSicht}
                    arbeitsunternehmenId={p.arbeitsunternehmenId ?? null}
                    vorbelegung={vorbelegung}
                    onRechnungAusAuftrag={rechnungAusAuftrag}
                    oeffneAuftragId={p.oeffneVorgangId ?? null}
                    {...jumpSource}
                    kundeName={p.kunde.companyName}
                    istAdmin={p.auftraege.istAdmin === true} onOrdnerOeffnen={openFolder} />
                : aktiv.id === "rechnungen"
                  // `kundeName` auch hier allein für die Platzhalter-Vorschau der
                  // Rechnungstexte (#{kunde.name}).
                  ? <KundeRechnungen kundeId={p.kunde.id} client={p.rechnungen.client}
                      companies={kundenUnternehmen} companyIds={belegSicht}
                      oeffneRechnungId={neueRechnungId}
                      oeffneVorgangId={p.oeffneVorgangId ?? null}
                      kundeName={p.kunde.companyName} onOrdnerOeffnen={openFolder} />
                  : aktiv.id === "liefervertraege" && p.liefervertraege !== undefined
                    // `key` am Kunden wie bei den Adressen: Liste, Aufgeklapptes und Panels gehören zu einem Kunden.
                    ? <KundeLiefervertraege key={p.kunde.id} client={p.liefervertraege.client} {...supplierTabProps} />
                  : aktiv.id === "eingangsrechnungen" && p.eingangsrechnungen !== undefined
                    ? <KundeEingangsrechnungen key={p.kunde.id} client={p.eingangsrechnungen.client} {...supplierTabProps} />
                  : aktiv.id === "tickets" && p.tickets !== undefined
                    // `key` am Kunden wie bei den Adressen: Liste und Anlege-Dialog halten
                    // eigenen Zustand, der den Kundenwechsel nicht überleben soll.
                    ? <KundeTickets key={p.kunde.id} client={p.tickets.client} companies={companies}
                        kunde={p.kunde} arbeitsunternehmenId={p.arbeitsunternehmenId ?? null} ich={p.tickets.ich} />
                  : aktiv.id === "wichtiges"
                    ? <KundeWichtiges kundeId={p.kunde.id} {...p.wichtiges} />
                    : aktiv.id === "notizen"
                      ? <KundeNotizen kundeId={p.kunde.id} unternehmen={zuordnung} oeffneNotizId={p.oeffneVorgangId ?? null}
                        {...p.notizen} />
                      : aktiv.id === "dateien"
                        ? <KundeDateien kundeId={p.kunde.id} unternehmen={zuordnung} oeffneDateiId={p.oeffneVorgangId ?? null}
                          oeffneOrdnerId={p.oeffneOrdnerId ?? null} {...p.dateien} />
                        : aktiv.id === "uebersicht"
                          // Ohne Klient nur die Kopfkachel. `key` am Kunden: Abruf, Ansage und Monitor
                          // beginnen bei jedem Kunden neu.
                          ? p.uebersicht === undefined
                            ? <HeaderCard customer={p.kunde} companies={companies} contact={{ kind: "failed" }} onTab={p.onTab} />
                            : <CustomerOverview key={p.kunde.id} customer={p.kunde} companies={companies}
                                customerCompanies={kundenUnternehmen} activeCompanyId={p.arbeitsunternehmenId ?? null}
                                extraCompanyIds={belegZusatz} onExtraCompanyIds={setBelegZusatz} viewCompanyIds={belegSicht}
                                client={p.uebersicht.client} monitorClient={p.monitor.client}
                                onTab={p.onTab} onTarget={(t, label, nachladen) => followTarget(t, label,
                                  { ...targetHandlers, oeffne: (b) => oeffneObjekt(b, nachladen) })} />
                          : <p className="ka__platzhalter">Dieser Bereich kommt mit Paket {aktiv.paket}.</p>}
      </div>
    </div>
  );
}
